import React from 'react';
import type { FiscalSettings } from '../../types/settings';

interface FiscalSettingsProps {
  settings: FiscalSettings;
  onSave: (data: FiscalSettings) => void;
  isLoading: boolean;
}

const FiscalSettings: React.FC<FiscalSettingsProps> = ({
  settings,
  onSave,
  isLoading
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    const data: FiscalSettings = {
      companyType: formData.get('companyType') as FiscalSettings['companyType'],
      taxRegime: formData.get('taxRegime') as string,
      defaultCFOP: {
        sale: formData.get('cfopSale') as string,
        return: formData.get('cfopReturn') as string,
        bonus: formData.get('cfopBonus') as string,
      },
      allowWeekendInvoices: formData.get('allowWeekendInvoices') === 'true',
      requireNCMValidation: formData.get('requireNCMValidation') === 'true',
      requireCSOSNValidation: formData.get('requireCSOSNValidation') === 'true',
    };

    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Configurações Fiscais
        </h2>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tipo de Empresa
            </label>
            <select
              name="companyType"
              defaultValue={settings.companyType}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="MEI">MEI</option>
              <option value="Simples">Simples Nacional</option>
              <option value="LucroPresumido">Lucro Presumido</option>
              <option value="LucroReal">Lucro Real</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Regime Tributário
            </label>
            <input
              type="text"
              name="taxRegime"
              defaultValue={settings.taxRegime}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              CFOP Padrão - Venda
            </label>
            <input
              type="text"
              name="cfopSale"
              defaultValue={settings.defaultCFOP.sale}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              CFOP Padrão - Devolução
            </label>
            <input
              type="text"
              name="cfopReturn"
              defaultValue={settings.defaultCFOP.return}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              CFOP Padrão - Bonificação
            </label>
            <input
              type="text"
              name="cfopBonus"
              defaultValue={settings.defaultCFOP.bonus}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              name="allowWeekendInvoices"
              defaultChecked={settings.allowWeekendInvoices}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Permitir emissão de notas fiscais em finais de semana
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="requireNCMValidation"
              defaultChecked={settings.requireNCMValidation}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Exigir validação de NCM nos produtos
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="requireCSOSNValidation"
              defaultChecked={settings.requireCSOSNValidation}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Exigir validação de CSOSN nos produtos
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Restaurar Padrões
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>
    </form>
  );
};

export default FiscalSettings;